import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import { authService } from './services/authService'
import { registerMicroApps, start } from '../vendor/local-qiankun/qiankun/es'
import 'nprogress/nprogress.css'
import Antd from 'ant-design-vue'
import '../index.less'
import microApps from './services/micro-app'
import NProgress from 'nprogress'
import "./interceptors/interceptor";

declare let __webpack_nonce__: string

const cspNonce = document
  .querySelector('meta[name="csp-nonce"]')
  ?.getAttribute('content')

if (cspNonce && cspNonce !== '__CSP_NONCE__') {
  (window as any).__cspNonce = cspNonce
  __webpack_nonce__ = cspNonce
  void __webpack_nonce__
}

/* ================= NProgress config ================= */

NProgress.configure({
  minimum: 0.08,
  easing: 'linear',
  positionUsing: '',
  speed: 200,
  trickle: true,
  trickleSpeed: 200,
  showSpinner: true,
  barSelector: '[role="bar"]',
  spinnerSelector: '[role="spinner"]',
  parent: 'body',
  template:
    '<div class="bar ttds-csp-spinner-bar" role="bar"><div class="peg"></div></div>' +
    '<div class="spinner ttds-csp-spinner" role="spinner">' +
    '<img class="ttds-csp-spinner-img" src="/images/loader.gif"></div></div>'
})

/* ================= Create & mount Vue app FIRST ================= */

const app = createApp(App)

app.config.globalProperties.$auth = authService
app.use(router)
app.use(Antd)
app.mount('#app')

/* ================= AUTH + QIANKUN BOOTSTRAP ================= */

async function bootstrap() {
  NProgress.start()
  try {
    await new Promise(resolve => setTimeout(resolve, 5000));
    // 1️⃣ Get user / Silent SSO
    let user = await authService.getUser()
    // !user && route.query.itp === 'true'
    if (!user) {
      user = await authService.signinSilent().catch(() => null)
      if (!user) {
        authService.signinRedirect()
        return
      }
    }

    // 2️⃣ USER CONTEXT
    const contextRes = await fetch(
      `${process.env.VUE_APP_apiGatewayRestURL}api/getusercontext`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.access_token}`
        }
      }
    )

    const redisInfo = await contextRes.json()

    const originalUserRoles = JSON.parse(redisInfo.roles)
    const userRoles: any[] = []
    const rolesOrder = ['2', '3', '4', '1']

    for (const roleId of rolesOrder) {
      const role = originalUserRoles.find((r: any) => r.RoleId == roleId)
      if (role) userRoles.push(role)
    }

    // store context
    sessionStorage.setItem('OIDC_USER', JSON.stringify(user))
    sessionStorage.setItem('Username', redisInfo.userName)
    sessionStorage.setItem('LoggedinUserId', redisInfo.userId)
    sessionStorage.setItem('userRoles', JSON.stringify(userRoles))
    sessionStorage.setItem('token', user.access_token)
    sessionStorage.setItem('id_token', user.id_token)
    sessionStorage.setItem('tenantId', redisInfo.tenantId)
    sessionStorage.setItem('roleId', userRoles[0]?.RoleId)
    sessionStorage.setItem('UserInfo', JSON.stringify(redisInfo))

    if (sessionStorage.getItem('roleId') === '1') {
      const permissions = ['ADMIN_CLIENT_ONBOARDING', 'ADMIN_MASTERS', 'ADMIN_TDS_SECTION', 'ADMIN_TCS_SECTION', 'ADMIN_REASONING']
      const modules = ['SuperAdmin']
      sessionStorage.setItem('UserPermissions', JSON.stringify(permissions))
      sessionStorage.setItem('UserModules', JSON.stringify(modules))
    } else {
      // 3️⃣ ROLE PERMISSIONS
      const permRes = await fetch(
        `${process.env.VUE_APP_apiGatewayRestURL}api/administration/rolepermissions/byroleid?id=${userRoles[0]?.RoleId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.access_token}`
          }
        }
      )

      const userPermission = await permRes.json()
      const permissions = userPermission.permissions
      const modules = userPermission.modules

      // 🚫 NO PERMISSIONS → LOGOUT
      if (!permissions || permissions.length === 0 || permissions[0] === '') {
        authService.signout()
        return
      }

      // store permissions
      sessionStorage.setItem('UserPermissions', JSON.stringify(permissions))
      sessionStorage.setItem('UserModules', JSON.stringify(modules))
      
      const res = await fetch(
        `${process.env.VUE_APP_apiGatewayRestURL}api/administration/entity/byuserID?FK_UserID=${redisInfo.userId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${user.access_token}`
          }
        }
      )

      const userInfo = await res.json()

      const userPans: any[] = []
      const userTans: any[] = []

      userInfo.forEach((element: any) => {
        userPans.push({
          Id: element.pK_EntitiyID,
          Name: `${element.entityName} - ${element.entityPAN}`,
          entityName: element.entityName,
          value: element.entityPAN,
          tdsMappedTemplateTypes: element.tdsMappedTemplateTypes,
          tcsMappedTemplateTypes: element.tcsMappedTemplateTypes,
          form26MappedTemplateTypes: element.form26MappedTemplateTypes
        })

        if (element.tanDetails?.length) {
          element.tanDetails.forEach((tanInfo: any) => {
            userTans.push({
              entityId: element.pK_EntitiyID,
              Name: `${tanInfo.tanName} - ${tanInfo.tan}`,
              value: tanInfo.tan,
              pan: element.entityPAN
            })
          })
        }
      })

      sessionStorage.setItem('userPans', JSON.stringify(userPans))
      sessionStorage.setItem('userTans', JSON.stringify(userTans))
    }
    
    // 🔑 AUTH READY SIGNAL
    sessionStorage.setItem('AUTH_READY', 'true')
    window.dispatchEvent(new Event('auth-ready'))

    sessionStorage.removeItem('QIANKUN_DISABLED')

    // 4️⃣ NOW SAFE TO LOAD MICRO APPS
    const apps = microApps.map((item: any) => ({
      ...item
    }))

    registerMicroApps(apps, {
      beforeLoad(): any {
        NProgress.start()
      },
      beforeMount(): any {
        NProgress.start()
      },
      afterMount(): any {
        NProgress.done()
      },
      afterUnmount(): any {
        NProgress.start()
      }
    })

    start({ prefetch: false })

  } catch {
    NProgress.done()
  }
}

/* ================= Start everything ================= */

bootstrap()